const { QRCode, QRCodeEncoder } = require('./qrcode');

/**
 * CLASS SquareQRCodeEncoder
 * While it can render an SVG, it mainly provides a base encoder for MarketQRCodeEncoder and EmbedQRCodeEncoder.
 * It adds these features to QRCodeEncoder:
 *   - svg rendering using the Turtle to edge trace data
 *   - hooks for adjusting the data for turtle rendering
 *   - using lowestPenalty calculation to make sure the code can be read
 *   - some base methods for SVG rendering
 * The following options can be specified:
 *   border          boolean (default false) whether to add a 4-pixel quiet zone
 *   invert          boolean (default false) light data on dark background when on
 *   styleBackground string to override style inserted in SVG background rectangle
 *   styleForeground string to override style inserted in SVG foreground data and patterns
 */
class SquareQRCodeEncoder extends QRCodeEncoder {
  encode() {
    this.initializeEncoding();
    for (const errorCorrection of this.errorCorrections) {
      this.code.errorCorrection = errorCorrection;
      this.initializeVersion();
      if (this.code.version < this.minimumVersion) continue;
      this.initializeCodewords();
      this.initializeMapping();
      this.initializeMasking();
      if (this.lowestPenalty < this.constructor.PENALTY_FOR_NEGATIVE_CAPACITY) {
        this.initializeTurtleData();
        this.initializeSvg();
        return;
      }
    }
    // go up in version, keep H errorCorrection
    const start = Math.max(this.minimumVersion, this.code.version + 1);
    for (let version = start; version <= QRCode.MAX_VERSION; version++) {
      this.code.version = version;
      this.initializeCodewords();
      this.initializeMapping();
      this.initializeMasking();
      if (this.lowestPenalty < this.constructor.PENALTY_FOR_NEGATIVE_CAPACITY) {
        this.initializeTurtleData();
        this.initializeSvg();
        return;
      }
    }
  }

  initializeSvg() {
    const offset = this.svgOffset;
    const dimension = this.svgDimension;
    const foregroundStyle = this.svgForegroundStyle;
    const backgroundStyle = this.svgBackgroundStyle;
    const source = [];
    source.push(this.svgTag(dimension));
    source.push('<defs>');
    source.push(this.svgFinderPattern(foregroundStyle));
    source.push('</defs>');
    source.push(this.svgBackground(backgroundStyle, dimension, offset === 0 ? undefined : offset / 2));
    this.svgFinderPatterns(source, offset);
    this.svgTurtle(source, foregroundStyle, offset);
    source.push('</svg>');
    this.svg = source.join('');
  }

  initializeTurtleData() {
    // add timing and alignment patterns as data
    const data = this.code.data.copy();
    for (const point of this.code.timingPatternPositions) {
      data.put(point.x, point.y, true);
    }
    for (const point of this.code.alignmentPatternPositions) {
      data.fillAlignmentPatternAt(point.x, point.y);
    }
    this.turtleData = data;
  }

  get inverted() {
    const inverted = this.code.options.invert;
    if (inverted === undefined) return false;
    return inverted;
  }

  get hasBorder() {
    const hasBorder = this.code.options.border;
    if (hasBorder === undefined) return false;
    return hasBorder;
  }

  get minimumVersion() {
    return 1;
  }

  async png() {
    if (this._png !== undefined) return this._png;
    if (this.svg === undefined) throw 'no SVG to convert to PNG';
    const image = new Image();
    const imageBlob = new Blob([this.svg], { type: 'image/svg+xml;charset=utf-8' });
    image.src = URL.createObjectURL(imageBlob);
    await image.decode();
    const dimension = this.svgDimension;
    const canvas = document.createElement('canvas');
    canvas.width = dimension;
    canvas.height = dimension;
    canvas.getContext('2d').drawImage(image, 0, 0, dimension, dimension);
    this._png = canvas.toDataURL();
    return this._png;
  }

  svgBackground(style, dimension, cornerRadius) {
    if (style === '') return '';
    return cornerRadius === undefined
      ? `<rect ${style} x="0" y="0" width="${dimension}" height="${dimension}" />`
      : `<rect ${style} x="0" y="0" width="${dimension}" height="${dimension}" rx="${cornerRadius}" ry="${cornerRadius}" />`;
  }

  get svgBackgroundStyle() {
    const fromOptions = this.code.options.styleBackground;
    if (fromOptions !== undefined) return fromOptions;
    return this.inverted ? 'fill="black"' : 'fill="white"';
  }

  get svgDimension() {
    return 2 * this.svgOffset + 8 * this.code.dimension;
  }

  svgFinderPattern(style) {
    return `<path id="f" ${style} fill-rule="evenodd" d="M 0 10 a10,10 0 0 1 10,-10 h36 a10,10 0 0 1 10,10 v36 a10,10 0 0 1 -10,10 h-36 a10,10 0 0 1 -10,-10 Z M 8 10 a2,2 0 0 1 2,-2 h36 a2,2 0 0 1 2,2 v36 a2,2 0 0 1 -2,2 h-36 a2,2 0 0 1 -2,-2 Z M 16 18 a2,2 0 0 1 2,-2 h20 a2,2 0 0 1 2,2 v20 a2,2 0 0 1 -2,2 h-20 a2,2 0 0 1 -2,-2 Z" />`;
  }

  svgFinderPatterns(source, offset) {
    for (const point of this.code.finderPatternPositions) {
      source.push(`<use xlink:href="#f" x="${point.x * 8 + offset}" y="${point.y * 8 + offset}" />`);
    }
  }

  get svgForegroundStyle() {
    const fromOptions = this.code.options.styleForeground;
    if (fromOptions !== undefined) return fromOptions;
    return this.inverted ? 'fill="white"' : 'fill="black"';
  }

  get svgOffset() {
    return this.hasBorder ? 32 : 0;
  }

  svgTag(dimension) {
    return `<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${dimension}" height="${dimension}" viewBox="0 0 ${dimension} ${dimension}">`;
  }

  svgTurtle(source, style, offset) {
    const dimension = this.code.dimension;
    for (let y = 0; y < dimension; y++) {
      for (let x = 0; x < dimension; x++) {
        if (this.turtleData.at(x, y)) {
          source.push(new Turtle(this.turtleData, style, offset, x, y).element);
        }
      }
    }
  }
}
// static variables
SquareQRCodeEncoder.PENALTY_FOR_LEFTOVER_CAPACITY = [32, 16, 8, 4, 2, 1];
SquareQRCodeEncoder.PENALTY_FOR_NEGATIVE_CAPACITY = 10000;

/**
 * CLASS Turtle
 * This is used to draw a path at a point, drawing both the outline and any cutouts.
 *   data            the BooleanMatrix of encoded data, along with the format and version information
 *   style           injected into the SVG element
 *   offset          how many SVG pixels to offset the topleft corner
 *   x               the x coordinate of the pixel to start from
 *   y               the y coordinate of the pixel to start from
 * It is only exported to be used in tests.
 */
class Turtle {
  constructor(data, style, offset, x, y) {
    this.data = data;
    this.dimension = data.width;
    this.style = style || '';
    this.offset = offset || 0;
    this.start = { x, y };
    this.visitedLocations = new Set();
    this.generateElement();
    this.clearVisited();
  }

  clearVisited() {
    this.visitedLocations.forEach((location) => {
      const point = this.fromLocation(location);
      this.data.put(point.x, point.y, false);
    });
  }

  get element() {
    return this._element;
  }

  fromLocation(location) {
    const x = location % this.dimension;
    const y = (location - x) / this.dimension;
    return { x, y };
  }

  generateElement() {
    // build outside edges
    const startX = this.start.x;
    const startY = this.start.y;
    const startEdge = 'left';
    let x = startX;
    let y = startY;
    const outsideEdges = [];
    outsideEdges.push(startEdge);
    let edge = 'top';
    const tops = new Set();
    const rights = new Set();
    const bottoms = new Set();
    const lefts = new Set();
    lefts.add(this.toLocation(x, y));
    while (!(x === startX && y === startY && edge === startEdge)) {
      outsideEdges.push(edge);
      const location = this.toLocation(x, y);
      this.visitLocation(location);
      switch (edge) {
        case 'top':
          tops.add(location);
          if (!this.hasPixelAtCoordinate(x + 1, y)) {
            edge = 'right';
          } else if (this.hasPixelAtCoordinate(x + 1, y - 1)) {
            x += 1;
            y -= 1;
            edge = 'left';
          } else {
            x += 1;
          }
          break;
        case 'right':
          rights.add(location);
          if (!this.hasPixelAtCoordinate(x, y + 1)) {
            edge = 'bottom';
          } else if (this.hasPixelAtCoordinate(x + 1, y + 1)) {
            x += 1;
            y += 1;
            edge = 'top';
          } else {
            y += 1;
          }
          break;
        case 'bottom':
          bottoms.add(location);
          if (!this.hasPixelAtCoordinate(x - 1, y)) {
            edge = 'left';
          } else if (this.hasPixelAtCoordinate(x - 1, y + 1)) {
            x -= 1;
            y += 1;
            edge = 'right';
          } else {
            x -= 1;
          }
          break;
        case 'left':
          lefts.add(location);
          if (!this.hasPixelAtCoordinate(x, y - 1)) {
            edge = 'top';
          } else if (this.hasPixelAtCoordinate(x - 1, y - 1)) {
            x -= 1;
            y -= 1;
            edge = 'bottom';
          } else {
            y -= 1;
          }
          break;
      }
    }
    // render outside edges
    if (outsideEdges.length === 4) {
      // if it only a single pixel, just make it a rectangle
      this._element = `<rect ${this.style} x="${startX * 8 + this.offset}" y="${
        startY * 8 + this.offset
      }" width="8" height="8" rx="2" ry="2" />`;
      return;
    }
    let d = [];
    d.push(`M${startX * 8 + this.offset + 1} ${startY * 8 + this.offset + 4}`);
    let next = outsideEdges[0];
    outsideEdges.push(next);
    let now;
    let length = 0;
    for (let i = 1; i < outsideEdges.length; i++) {
      now = next;
      next = outsideEdges[i];
      if (now === next) {
        length += 8;
      } else if (now === 'top' && next === 'right') {
        if (length !== 0) {
          d.push(`h${length}`);
          length = 0;
        }
        d.push('a3,3 0 0 1 3,3');
      } else if (now === 'right' && next === 'bottom') {
        if (length !== 0) {
          d.push(`v${length}`);
          length = 0;
        }
        d.push('a3,3 0 0 1 -3,3');
      } else if (now === 'bottom' && next === 'left') {
        if (length !== 0) {
          d.push(`h-${length}`);
          length = 0;
        }
        d.push('a3,3 0 0 1 -3,-3');
      } else if (now === 'left' && next === 'top') {
        if (length !== 0) {
          d.push(`v-${length}`);
          length = 0;
        }
        d.push('a3,3 0 0 1 3,-3');
      } else if (now === 'top' && next === 'left') {
        d.push(`h${length + 5}`);
        length = 5;
      } else if (now === 'right' && next === 'top') {
        d.push(`v${length + 5}`);
        length = 5;
      } else if (now === 'bottom' && next === 'right') {
        d.push(`h-${length + 5}`);
        length = 5;
      } else if (now === 'left' && next === 'bottom') {
        d.push(`v-${length + 5}`);
        length = 5;
      }
    }
    if (length > 0) d.push(`v-${length}`);
    d.push('z');
    // determine the interior pixels
    let interior = new Set();
    let process = new Set();
    this.visitedLocations.forEach((location) => {
      if (!tops.has(location)) process.add(location - this.dimension);
      if (!lefts.has(location)) process.add(location - 1);
      if (!bottoms.has(location)) process.add(location + this.dimension);
      if (!rights.has(location)) process.add(location + 1);
    });
    process = [...process].filter((location) => !this.hasVisitedLocation(location));
    while (process.length > 0) {
      let fanOutFrom = [];
      process.forEach((location) => {
        if (this.hasPixelAtLocation(location)) {
          this.visitLocation(location);
          fanOutFrom.push(location);
        } else {
          interior.add(location);
        }
      });
      process = new Set();
      fanOutFrom.forEach((location) => {
        process.add(location - this.dimension);
        process.add(location + this.dimension);
        process.add(location + 1);
        process.add(location - 1);
      });
      process = [...process].filter((location) => !this.hasVisitedLocation(location));
    }
    while (interior.size > 0) {
      // determine and move to the top right
      let topRight, topRightX, topRightY;
      interior.forEach((location) => {
        x = location % this.dimension;
        y = (location - x) / this.dimension;
        if (topRight === undefined || y < topRightY || (y === topRightY && x > topRightX)) {
          topRight = location;
          topRightX = x;
          topRightY = y;
        }
      });
      d.push(`M${topRightX * 8 + this.offset + 9} ${topRightY * 8 + this.offset - 1}`);
      // move counterclockwise to remove interior
      x = topRightX;
      y = topRightY;
      edge = 'top';
      let distance = 5;
      while (!(x === topRightX && y === topRightY && edge === 'right')) {
        const location = this.toLocation(x, y);
        interior.delete(location);
        switch (edge) {
          case 'top':
            if (!this.hasPixelAtCoordinate(x - 1, y - 1)) {
              if (distance > 0) d.push(`h-${distance}`);
              d.push('a3,3 0 0 1 -3,-3');
              distance = 0;
              edge = 'right';
              x--;
              y--;
            } else if (!this.hasPixelAtCoordinate(x - 1, y)) {
              distance += 8;
              x--;
            } else {
              d.push(`h-${distance + 5}`);
              distance = 5;
              edge = 'left';
            }
            break;
          case 'left':
            if (!this.hasPixelAtCoordinate(x - 1, y + 1)) {
              if (distance > 0) d.push(`v${distance}`);
              d.push('a3,3 0 0 1 -3,3');
              distance = 0;
              edge = 'top';
              x--;
              y++;
            } else if (!this.hasPixelAtCoordinate(x, y + 1)) {
              distance += 8;
              y++;
            } else {
              d.push(`v${distance + 5}`);
              distance = 5;
              edge = 'bottom';
            }
            break;
          case 'bottom':
            if (!this.hasPixelAtCoordinate(x + 1, y + 1)) {
              if (distance > 0) d.push(`h${distance}`);
              d.push('a3,3 0 0 1 3,3');
              distance = 0;
              edge = 'left';
              x++;
              y++;
            } else if (!this.hasPixelAtCoordinate(x + 1, y)) {
              distance += 8;
              x++;
            } else {
              d.push(`h${distance + 5}`);
              distance = 5;
              edge = 'right';
            }
            break;
          case 'right':
            if (!this.hasPixelAtCoordinate(x + 1, y - 1)) {
              if (distance > 0) d.push(`v-${distance}`);
              d.push('a3,3 0 0 1 3,-3');
              distance = 0;
              edge = 'bottom';
              x++;
              y--;
            } else if (!this.hasPixelAtCoordinate(x, y - 1)) {
              distance += 8;
              y--;
            } else {
              d.push(`v-${distance + 5}`);
              distance = 5;
              edge = 'top';
            }
            break;
        }
      }
      d.push(`v-${distance + 5}`);
      d.push('z');
    }
    this._element = `<path ${this.style} d="${d.join('')}" />`;
  }

  hasPixelAtCoordinate(x, y) {
    if (x >= this.dimension || x < 0 || y >= this.dimension || y < 0) return false;
    return this.data.at(x, y);
  }

  hasPixelAtLocation(location) {
    const x = location % this.dimension;
    const y = (location - x) / this.dimension;
    return this.data.at(x, y);
  }

  hasVisitedCoordinate(x, y) {
    return this.hasVisitedLocation(this.toLocation(x, y));
  }

  hasVisitedLocation(location) {
    return this.visitedLocations.has(location);
  }

  toLocation(x, y) {
    return this.dimension * y + x;
  }

  visitCoordinate(x, y) {
    this.visitLocation(this.toLocation(x, y));
  }

  visitLocation(location) {
    this.visitedLocations.add(location);
  }
}

module.exports = { SquareQRCodeEncoder, Turtle };
